import {
  Col,
  Row,
  TableColumnsType,
  Tooltip,
  Button as ButtonAnt,
  Typography,
  Select,
  Input,
  message,
  Modal
} from 'antd';
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { Table } from '@/components/tables/table';
import { css } from '@emotion/react';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { NutritionStandardsManagementSearcher } from '@/components/forms/nutrition-standards-management/searcher.form';
import {
  UnitMode,
  MealMode,
  NutritionStandardKey
} from '@/enums/nutrition-standard.enum';
import { ColumnsType } from 'antd/es/table';
import {
  createNutritionStandard,
  deleteNutritionStandard,
  editNutritionStandard,
  getFilteredNutritionStandards,
  getListAgeNutritionStandard,
  getListGenderNutritionStandard,
  getListHealthStatusNutritionStandard,
  getListHealthStatusZCoreNutritionStandard,
  getListPhysicalActivityNutritionStandard,
  watchFilteredNutritionalStandards,
  watchListAgeNutritionStandard,
  watchListGenderNutritionStandard,
  watchListHealthStatusNutritionStandard,
  watchListHealthStatusZCoreNutritionStandard,
  watchListPhysicalActivityNutritionStandard,
  watchNutritionStandardsPagination,
  watchMessage,
  resetMessages,
  getBreastMilkLevels,
  watchListBreastMilkLevel,
  watchNutritionLoading
} from '@/redux/slices/nutrition-standards-management.slice';
import {
  mapApiToFilteredNutritionStandard,
  mapEditedDataToPayload,
  newRowStandardNutrition,
  newRowStandardNutritionId,
  UNDER_24_MONTHS_AGE_IDS_EXCLUDE_DT00
} from '@/utils/nutrition-columns.utils';
import { AGE_TYPE_CONST } from '@/constants/type';
import { setMessages } from '@/redux/slices/app.slice';
import { handlePositiveNumberInputKeyDown, handleDecimalInputChange, removeVietnameseTones, handlePositiveIntegerInputKeyDown } from '@/utils/common';
import { getEditableCellProps } from './utils/editableCellProps';

// Format number for display (129.5 -> 129,5)
const formatNumberDisplay = (value: any): string => {
  if (value === undefined || value === null || value === '') return '';
  return String(value).replace('.', ',');
};

// List of keys that need decimal formatting (from Kali onwards)
const decimalFormatKeys = [
  'fiberStandardDay',
  'kaliStandardDay',
  'cholesterolStandardDay',
  'natriStandardDay',
  'cloStandardDay',
  'calciStandardDay',
  'phosphoStandardDay',
  'magieStandardDay',
  'ironStandardDay',
  'zincStandardDay',
  'iodStandardDay',
  'selenStandardDay',
  'copperStandardDay',
  'cromStandardDay',
  'manganStandardDay',
  'fluorStandardDay',
  'vitaminAStandardDay',
  'vitaminDStandardDay',
  'vitaminEStandardDay',
  'vitaminKStandardDay',
  'vitaminB1StandardDay',
  'vitaminB2StandardDay',
  'vitaminB3StandardDay',
  'vitaminB5StandardDay',
  'vitaminB6StandardDay',
  'vitaminB9StandardDay',
  'vitaminB12StandardDay',
  'biotinStandardDay',
  'cholineStandardDay',
  'vitaminCStandardDay',
  'totalUnsaturated'
];

// Format record for editing - convert decimal points to commas
const formatRecordForEdit = (record: any) => {
  const formatted = { ...record };
  decimalFormatKeys.forEach(key => {
    if (formatted[key] !== undefined && formatted[key] !== null && formatted[key] !== '') {
      formatted[key] = formatNumberDisplay(formatted[key]);
    }
  });
  return formatted;
};

const minMaxNutrition = [
  { key: 'min', name: 'Min' },
  { key: 'max', name: 'Max' }
];

const mealsPerDay = [
  { key: MealMode.BREAKFAST, name: 'Sáng', data: minMaxNutrition },
  { key: MealMode.LUNCH, name: 'Trưa', data: minMaxNutrition },
  { key: MealMode.AFTERNOON_MEAL, name: 'Chiều', data: minMaxNutrition },
  { key: MealMode.DINNER, name: 'Tối', data: minMaxNutrition },
  { key: MealMode.EXTRA_MEAL_1, name: 'Phụ 1', data: minMaxNutrition },
  { key: MealMode.EXTRA_MEAL_2, name: 'Phụ 2', data: minMaxNutrition }
];

const mealsDay = [
  { key: MealMode.STRUCT, name: 'Cấu trúc bữa' },
  { key: MealMode.DAY, name: 'Cả ngày' },
  { key: MealMode.BREAKFAST, name: 'Bữa sáng (g)' },
  { key: MealMode.LUNCH, name: 'Bữa trưa (g)' },
  { key: MealMode.AFTERNOON_MEAL, name: 'Bữa chiều (g)' },
  { key: MealMode.DINNER, name: 'Bữa tối (g)' },
  { key: MealMode.EXTRA_MEAL, name: 'Phụ (g)' }
];

const CountUnit = [
  { key: 'percent', name: '%', data: minMaxNutrition },
  { key: 'gram', name: 'g', data: minMaxNutrition }
];

const unitNutrition = [
  { key: UnitMode.P, name: 'P', data: CountUnit },
  { key: UnitMode.L, name: 'L', data: CountUnit },
  { key: UnitMode.G, name: 'G', data: CountUnit }
];

const allNutritionStandard = [
  {
    key: NutritionStandardKey.STANDARD_PLG_DAY,
    name: 'Tiêu chuẩn PLG theo ngày',
    data: unitNutrition,
    className: 'redStyle',
    width: 300
  },
  {
    key: NutritionStandardKey.STANDARD_MEALS_PER_DAY,
    name: 'Số lượng bữa/ngày',
    className: 'redStyle',
    width: 100
  },
  {
    key: NutritionStandardKey.STANDARD_ENERGY_DISTRIBUTION,
    name: 'Tiêu chuẩn phân bổ năng lượng cho các bữa trong ngày (%)',
    data: mealsPerDay,
    className: 'purpleStyle',
    width: 1200
  },
  {
    key: NutritionStandardKey.STANDARD_PLG_BREAKFAST,
    name: 'Tiêu chuẩn tỉ lệ PLG bữa sáng',
    data: unitNutrition,
    className: 'purpleStyle',
    width: 200
  },
  {
    key: NutritionStandardKey.STANDARD_PLG_LUNCH,
    name: 'Tiêu chuẩn tỉ lệ PLG bữa trưa',
    data: unitNutrition,
    className: 'purpleStyle',
    width: 200
  },
  {
    key: NutritionStandardKey.STANDARD_PLG_AFTERNOON,
    name: 'Tiêu chuẩn tỉ lệ PLG bữa chiều',
    data: unitNutrition,
    className: 'purpleStyle',
    width: 200
  },
  {
    key: NutritionStandardKey.STANDARD_PLG_DINNER,
    name: 'Tiêu chuẩn tỉ lệ PLG bữa tối',
    data: unitNutrition,
    className: 'purpleStyle',
    width: 200
  },
  {
    key: NutritionStandardKey.STANDARD_PLG_EXTRA1,
    name: 'Tiêu chuẩn tỉ lệ PLG bữa phụ 1',
    data: unitNutrition,
    className: 'purpleStyle',
    width: 200
  },
  {
    key: NutritionStandardKey.STANDARD_FRUIT,
    name: 'Tiêu chuẩn quả chín (tối thiểu)',
    data: mealsDay,
    className: 'redStyle',
    width: 250
  },
  {
    key: NutritionStandardKey.STANDARD_VEGETABLE,
    name: 'Tiêu chuẩn rau củ (tối thiểu)',
    data: mealsDay,
    className: 'redStyle',
    width: 250
  },
  {
    key: NutritionStandardKey.STANDARD_SALT,
    name: 'Tiêu chuẩn muối (tối thiểu)',
    data: mealsDay,
    className: 'yellowStyle',
    width: 200
  }
];

const energyColumnName = [
  ['Chuyển hóa cơ bản (kcal/kg/ngày)', 'metabolismBasic', 'grayStyle'],
  ['Năng lượng tiêu hao/ngày (kcal)', 'energyConsumption', 'grayStyle'],
  ['Năng lượng cần cung cấp/ ngày (kcal)', 'energyNeeded', 'grayStyle']
];

const renderEditableCell = ({
  record,
  keyName,
  editingRowId,
  editedRows,
  setEditedRows,
  options,
  onAgeChange,
  listAge,
  placeholder,
  isDecimalFormat = false,
}: RenderEditableCellProps & { onAgeChange?: (rowId: string, ageId: string) => void, listAge?: NutritionAge[], placeholder?: string, isDecimalFormat?: boolean }) => {
  const isEditing = editingRowId === record.id;

  // handle breastMilkLevel
  let value: any;
  if (keyName === 'breastMilkLevel') {
    if (isEditing) {
      value = editedRows[record.id]?.[keyName];
    } else {
      value = record.breastMilkLevel !== undefined
        ? record.breastMilkLevel
        : null;
    }
  } else {
    value = isEditing ? editedRows[record.id]?.[keyName] : record[keyName];
  }

  if (!isEditing) {
    // Display operator symbol for proportion operator fields
    if (keyName === 'pProportionOperator' || keyName === 'lProportionOperator') {
      const operator = record[keyName];
      if (!operator || operator === '' || operator === null || operator === undefined) return '';
      // Convert operator to display symbol
      const symbolMap: Record<string, string> = {
        '>=': '≥',
        '<=': '≤',
        '>': '>',
        '<': '<',
        '=': '='
      };
      return symbolMap[operator] || operator;
    }
    // Display value for proportion value fields
    if (keyName === 'pProportionValue' || keyName === 'lProportionValue') {
      // Only display if value exists
      if (value === null || value === undefined || value === '') return '';
      return value;
    }
    if (options) {
      return options.find((opt) => opt.value === value)?.label ?? value;
    }
    return value;
  }

  const cellProps = getEditableCellProps({ keyName, record, editedRows, listAge, options });
  switch (cellProps.type) {
    case 'multi-select': {
      const { value: multiValue, options: multiOptions, isUnder19, required } = cellProps;
      const under19Options = (multiOptions || []).filter((opt: any) => opt.label === 'Bình thường');
      const displayOptions = isUnder19 ? under19Options : (multiOptions || []);
      return (
        <Select
          data-rowid={record.id}
          data-keyname={keyName}
          mode="multiple"
          options={displayOptions}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            removeVietnameseTones((option?.label ?? '').toString().toLowerCase())
              .includes(removeVietnameseTones(input.toLowerCase()))
          }
          value={multiValue}
          onChange={(vals) => {
            let newVals = vals;
            if (isUnder19) {
              // Nếu dưới 19 tuổi, chỉ cho phép chọn "Bình thường"
              newVals = under19Options.length > 0 ? [under19Options[0].value] : [];
            } else {
              // Nếu trên 19 tuổi, cho phép chọn nhiều
              newVals = vals;
            }
            setEditedRows((prev) => {
              const updated = {
                ...prev,
                [record.id]: {
                  ...prev[record.id],
                  [keyName]: newVals
                }
              };
              return updated;
            });
          }}
          placeholder={placeholder}
          style={{ width: 220 }}
          onBlur={() => {
            if (required && (!multiValue || multiValue.length === 0)) {
              message.warning('Bắt buộc chọn tình trạng sức khỏe');
            }
          }}
          allowClear={!isUnder19}
          disabled={isUnder19 && under19Options.length === 0}
        />
      );
    }
    case 'select': {
      const { value: selectValue, options: selectOptions = [], required, disabled } = cellProps;
      const selectWidth = (keyName === 'lProportionOperator' || keyName === 'pProportionOperator') ? 60 : 120;
      return (
        <Select
          data-rowid={record.id}
          data-keyname={keyName}
          value={selectValue}
          options={selectOptions}
          disabled={disabled}
          onChange={val => {
            setEditedRows(prev => ({
              ...prev,
              [record.id]: {
                ...prev[record.id],
                [keyName]: val
              }
            }));
          }}
          placeholder={placeholder}
          style={{ width: selectWidth }}
          allowClear={false}
          onBlur={() => {
            if (required && (selectValue === undefined || selectValue === null || selectValue === '')) {
              message.warning('Bắt buộc chọn giá trị');
            }
          }}
        />
      );
    }
    case 'integer-input': {
      const { value: intValue, min, max, required } = cellProps;
      return (
        <Input
          data-rowid={record.id}
          data-keyname={keyName}
          type="number"
          min={min}
          max={max}
          required={required}
          value={intValue === undefined || intValue === null ? '' : intValue}
          onKeyDown={handlePositiveIntegerInputKeyDown}
          onChange={e => {
            let val = e.target.value;
            if (!/^\d*$/.test(val)) return;
            if (val !== '' && Number(val) > (max ?? Infinity)) val = String(max);
            setEditedRows(prev => ({
              ...prev,
              [record.id]: {
                ...prev[record.id],
                [keyName]: val
              }
            }));
          }}
          onBlur={e => {
            const v = e.target.value;
            if (required && (v === '' || isNaN(Number(v)) || Number(v) < (min ?? 0) || Number(v) > (max ?? Infinity))) {
              if (min !== undefined && max !== undefined) {
                message.warning(`Giá trị bắt buộc, số nguyên từ ${min} đến ${max}`);
              } else if (min !== undefined) {
                message.warning(`Giá trị bắt buộc, số nguyên từ ${min} trở lên`);
              } else {
                message.warning('Giá trị bắt buộc');
              }
            }
          }}
        />
      );
    }
    case 'decimal-input': {
      const { value: decValue, min, max, required, disabled } = cellProps;
      const displayValue = isDecimalFormat && decValue !== undefined && decValue !== null && decValue !== ''
        ? formatNumberDisplay(decValue)
        : (decValue === undefined || decValue === null ? '' : decValue);
      return (
        <Input
          data-rowid={record.id}
          data-keyname={keyName}
          type="number"
          min={min}
          max={max}
          required={required}
          disabled={disabled}
          value={displayValue}
          onKeyDown={handlePositiveNumberInputKeyDown}
          onChange={(e) => {
            const rawValue = e.target.value;
            // Convert comma to dot for internal storage
            const normalizedValue = rawValue.replace(',', '.');
            handleDecimalInputChange({ ...e, target: { ...e.target, value: normalizedValue } }, max, setEditedRows, record, keyName);
          }}
          step="any"
          placeholder={placeholder}
          onBlur={e => {
            const v = e.target.value;
            if (keyName === 'milkUnitsPerDay') {
              return;
            }
            if (required && (v === '' || isNaN(Number(v)) || Number(v) < (min ?? 0) || Number(v) > (max ?? Infinity))) {
              if (min !== undefined && max !== undefined) {
                message.warning(`Giá trị bắt buộc, số từ ${min} đến ${max}`);
              } else if (min !== undefined) {
                message.warning(`Giá trị bắt buộc, số từ ${min} trở lên`);
              } else {
                message.warning('Giá trị bắt buộc');
              }
            }
          }}
        />
      );
    }
    case 'custom-input': {
      const { value, required, validate } = cellProps;
      const allowedPattern = /^[0-9.,><=-]*$/;
      return (
        <Input
          data-rowid={record.id}
          data-keyname={keyName}
          value={value === undefined || value === null ? '' : value}
          onChange={e => {
            const v = e.target.value;
            if (v === '' || allowedPattern.test(v)) {
              setEditedRows(prev => ({
                ...prev,
                [record.id]: {
                  ...prev[record.id],
                  [keyName]: v
                }
              }));
            }
          }}
          onBlur={e => {
            const v = e.target.value;
            if (required && !v) {
              message.warning('Trường này là bắt buộc');
            } else if (validate && !validate(v)) {
              message.warning('Sai định dạng!');
            }
          }}
        />
      );
    }
    case 'string-input': {
      const { value, required } = cellProps;

      return (
        <Input
          data-rowid={record.id}
          data-keyname={keyName}
          value={value ?? ''}
          onChange={e => {
            const v = e.target.value;
            setEditedRows(prev => ({
              ...prev,
              [record.id]: {
                ...prev[record.id],
                [keyName]: v,
              },
            }));
          }}
          onBlur={e => {
            const v = e.target.value;
            if (required && !v) {
              message.warning('Trường này là bắt buộc');
            }
          }}
        />
      );
    }

    case 'default':
    default: {
      if (options && keyName === 'ageRangeId' && onAgeChange) {
        return (
          <Select
            data-rowid={record.id}
            data-keyname={keyName}
            value={value !== undefined && value !== null && value !== '' ? value : undefined}
            onChange={(val) => {
              setEditedRows((prev) => ({
                ...prev,
                [record.id]: {
                  ...prev[record.id],
                  [keyName]: val
                }
              }));
              onAgeChange(record.id, val);
            }}
            style={{ width: 160 }}
            placeholder={placeholder}
            allowClear={false}
          >
            {options.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        );
      }

      if (options) {
        return (
          <Select
            data-rowid={record.id}
            data-keyname={keyName}
            value={value !== undefined && value !== null && value !== '' ? value : undefined}
            onChange={(val) => {
              setEditedRows((prev) => ({
                ...prev,
                [record.id]: {
                  ...prev[record.id],
                  [keyName]: val
                }
              }));
            }}
            onBlur={() => {
              const currentValue = editedRows[record.id]?.[keyName];
              if (
                currentValue === undefined ||
                currentValue === null ||
                currentValue === ''
              ) {
                message.warning('Trường này là bắt buộc, vui lòng chọn giá trị');
              }
            }}
            style={{ width: 160 }}
            placeholder={placeholder}
            allowClear={false}
          >
            {options.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        );
      }

      return (
        <Input
          data-rowid={record.id}
          data-keyname={keyName}
          type='number'
          min={0}
          required
          value={value === undefined || value === null ? '' : value}
          onKeyDown={handlePositiveNumberInputKeyDown}
          onChange={(e) => {
            const inputValue = e.target.value;
            const numberValue = Number(inputValue);
            setEditedRows((prev) => ({
              ...prev,
              [record.id]: {
                ...prev[record.id],
                [keyName]: inputValue === '' || numberValue < 0 ? '' : numberValue
              }
            }));
          }}
          onBlur={(e) => {
            const numberValue = Number(e.target.value);
            if (e.target.value === '' || isNaN(numberValue) || numberValue < 0) {
              message.warning('Giá trị bắt buộc và không được âm');
            }
          }}
        />
      );
    }
  }
};

const generateMinMaxColumns = ({
  baseKey,
  className,
  editingRowId,
  editedRows,
  setEditedRows,
  listAge
}: GenerateMinMaxColumnsParams & { listAge?: NutritionAge[] }): TableColumnsType<any> => {
  return ['Min', 'Max'].map((suffix) => {
    const fullKey = `${baseKey}${suffix}`;
    return {
      title: suffix,
      dataIndex: fullKey,
      key: fullKey,
      align: 'center' as const,
      className: className ?? 'white-header-text',
      width: 100,
      render: (_: any, record: any) =>
        renderEditableCell({
          record,
          keyName: fullKey,
          editingRowId: editingRowId ?? null,
          editedRows: editedRows ?? {},
          setEditedRows: setEditedRows ?? (() => { }),
          listAge
        })
    };
  });
};

const generateReserveColumns = (
  index: number,
  className?: string,
  editingRowId?: string | number | null,
  editedRows?: Record<string, any>,
  setEditedRows?: React.Dispatch<React.SetStateAction<Record<string, any>>>
): TableColumnsType<any> => {
  const baseKey = `backup${index}StandardDay`;

  return [
    {
      title: `Dự phòng ${index}`,
      key: baseKey,
      align: 'center' as const,
      className: className ?? 'grayStyle',
      width: 200,
      children: ['min', 'max'].map((suffix) => {
        const fullKey = `${baseKey}${suffix.charAt(0).toUpperCase()}${suffix.slice(1)}`;
        return {
          title: suffix === 'min' ? 'Min' : 'Max',
          dataIndex: fullKey,
          key: fullKey,
          align: 'center' as const,
          className: className ?? 'grayStyle',
          width: 100,
          render: (_: any, record: any) =>
            editingRowId && editedRows && setEditedRows
              ? renderEditableCell({
                record,
                keyName: fullKey,
                editingRowId,
                editedRows,
                setEditedRows,
                placeholder: suffix === 'min' ? 'Từ' : 'Đến'
              })
              : record[fullKey]
        };
      })
    }
  ];
};

const generateCountUnitColumns = (
  unit: string,
  baseKey: string,
  countKey: string,
  className?: string,
  editingRowId?: string | number | null,
  editedRows?: Record<string, any>,
  setEditedRows?: React.Dispatch<React.SetStateAction<Record<string, any>>>,
  listAge?: NutritionAge[]
) => {
  const key = `${baseKey}${countKey}`;
  return {
    title: unit,
    key,
    align: 'center' as const,
    className: className ?? 'white-header-text',
    width: 200,
    children: generateMinMaxColumns({
      baseKey: key,
      className,
      editingRowId,
      editedRows,
      setEditedRows,
      listAge
    })
  };
};
const generateUnitNutritionColumns = (
  item: any,
  className?: string,
  editingRowId?: string | number | null,
  editedRows?: Record<string, any>,
  setEditedRows?: React.Dispatch<React.SetStateAction<Record<string, any>>>,
  listAge?: NutritionAge[]
) => {
  return {
    title: item.name,
    align: 'center' as const,
    className: className ?? 'white-header-text',
    width: 200,
    children: item.data.map((countUnit: any) =>
      generateCountUnitColumns(
        countUnit.name,
        `${item.key}`,
        countUnit.key,
        className,
        editingRowId,
        editedRows,
        setEditedRows,
        listAge
      )
    )
  };
};

const generateDataColumns = (
  data: any,
  className?: string,
  editingRowId?: string | number | null,
  editedRows?: Record<string, any>,
  setEditedRows?: React.Dispatch<React.SetStateAction<Record<string, any>>>,
  listAge?: NutritionAge[]
) => {
  return {
    title: data.name,
    align: 'center' as const,
    className: className ?? 'white-header-text',
    width: 200,
    children:
      data.data?.map((mm: any) => ({
        title: mm.name,
        dataIndex: `${data.key}${mm.key}`,
        key: `${data.key}${mm.key}`,
        align: 'center',
        className: className ?? 'white-header-text',
        width: 100,
        render: (_: any, record: any) =>
          renderEditableCell({
            record,
            keyName: `${data.key}${mm.key}`,
            editingRowId: editingRowId ?? null,
            editedRows: editedRows ?? {},
            setEditedRows: setEditedRows ?? (() => { }),
            listAge
          })
      })) || []
  };
};

const generateMealsDayColumns = (
  nutritionKey: NutritionStandardKey,
  className?: string,
  editingRowId: string | number | null = null,
  editedRows: Record<string, any> = {},
  setEditedRows: React.Dispatch<
    React.SetStateAction<Record<string, any>>
  > = () => { },
  listAge?: NutritionAge[]
): ColumnsType<any> => {
  let filteredMeals = mealsDay;

  if (
    nutritionKey === NutritionStandardKey.STANDARD_FRUIT ||
    nutritionKey === NutritionStandardKey.STANDARD_VEGETABLE
  ) {
    filteredMeals = mealsDay.filter(
      (meal) => meal.key !== MealMode.STRUCT && meal.key !== MealMode.EXTRA_MEAL
    );
  }

  return filteredMeals.map((meal) => {
    const key = `${nutritionKey}${meal.key}`;
    if (nutritionKey === NutritionStandardKey.STANDARD_SALT && meal.key === MealMode.STRUCT) {
      return {
        title: meal.name,
        dataIndex: key,
        key,
        align: 'center' as const,
        className: className ?? 'white-header-text',
        width: 100,
        render: (_: any, record: any) => {
          const isEditing = editingRowId === record.id;
          const meals = editedRows[record.id]?.standardMealsPerDay ?? record.standardMealsPerDay;
          if (isEditing) {
            return <Input value={meals || ''} readOnly />;
          }
          return <span>{meals || ''}</span>;
        }
      };
    }
    return {
      title: meal.name,
      dataIndex: key,
      key,
      align: 'center' as const,
      className: className ?? 'white-header-text',
      width: 100,
      render: (_: any, record: any) =>
        renderEditableCell({
          record,
          keyName: key,
          editingRowId: editingRowId ?? null,
          editedRows,
          setEditedRows,
          listAge
        })
    };
  });
};

const generateNutritionColumns = (
  standard: any,
  editingRowId: string | number | null,
  editedRows: Record<string, any>,
  setEditedRows: React.Dispatch<React.SetStateAction<Record<string, any>>>,
  listAge?: NutritionAge[]
) => {
  if (!standard.data) {
    return {
      title: standard.name,
      dataIndex: standard.key,
      key: standard.key,
      align: 'center' as const,
      className: standard.className ?? 'white-header-text',
      width: standard.width,
      render: (_: any, record: any) =>
        renderEditableCell({
          record,
          keyName: standard.key,
          editingRowId: editingRowId,
          editedRows: editedRows,
          setEditedRows
        })
    };
  }

  if (standard.key === NutritionStandardKey.STANDARD_PLG_DAY) {
    return {
      title: standard.name,
      className: standard.className ?? 'white-header-text',
      width: 300,
      children: [
        generateUnitNutritionColumns(
          {
            key: `${standard.key}P`,
            name: 'P',
            data: CountUnit
          },
          standard.className,
          editingRowId,
          editedRows,
          setEditedRows,
          listAge
        ),
        {
          title: 'P đv/P tổng (%)',
          key: `${standard.key}PRatio`,
          align: 'center' as const,
          className: 'yellowStyle',
          width: 200,
          children: [
            {
              title: '%',
              key: 'pProportionGroup',
              align: 'center' as const,
              className: 'yellowStyle',
              width: 200,
              children: [
                {
                  title: '',
                  key: 'pProportionOperator',
                  dataIndex: 'pProportionOperator',
                  align: 'center' as const,
                  className: 'yellowStyle',
                  width: 100,
                  render: (_: any, record: any) =>
                    renderEditableCell({
                      record,
                      keyName: 'pProportionOperator',
                      editingRowId,
                      editedRows,
                      setEditedRows
                    })
                },
                {
                  title: '',
                  key: 'pProportionValue',
                  dataIndex: 'pProportionValue',
                  align: 'center' as const,
                  className: 'yellowStyle',
                  width: 100,
                  render: (_: any, record: any) =>
                    renderEditableCell({
                      record,
                      keyName: 'pProportionValue',
                      editingRowId,
                      editedRows,
                      setEditedRows
                    })
                }
              ]
            }
          ]
        },
        generateUnitNutritionColumns(
          {
            key: `${standard.key}L`,
            name: 'L',
            data: CountUnit
          },
          standard.className,
          editingRowId,
          editedRows,
          setEditedRows,
          listAge
        ),
        {
          title: 'L đv/L tổng (≥ %)',
          key: `${standard.key}_L_RATIO`,
          align: 'center' as const,
          className: 'yellowStyle',
          width: 200,
          children: [
            {
              title: '%',
              key: 'lProportionGroup',
              align: 'center' as const,
              className: 'yellowStyle',
              width: 200,
              children: [
                {
                  title: '',
                  key: 'lProportionOperator',
                  dataIndex: 'lProportionOperator',
                  align: 'center' as const,
                  className: 'yellowStyle',
                  width: 100,
                  render: (_: any, record: any) =>
                    renderEditableCell({
                      record,
                      keyName: 'lProportionOperator',
                      editingRowId,
                      editedRows,
                      setEditedRows
                    })
                },
                {
                  title: '',
                  key: 'lProportionValue',
                  dataIndex: 'lProportionValue',
                  align: 'center' as const,
                  className: 'yellowStyle',
                  width: 100,
                  render: (_: any, record: any) =>
                    renderEditableCell({
                      record,
                      keyName: 'lProportionValue',
                      editingRowId,
                      editedRows,
                      setEditedRows
                    })
                }
              ]
            }
          ]
        },
        generateUnitNutritionColumns(
          {
            key: `${standard.key}G`,
            name: 'G',
            data: CountUnit
          },
          standard.className,
          editingRowId,
          editedRows,
          setEditedRows,
          listAge
        )
      ]
    };
  }

  if (standard.key === NutritionStandardKey.STANDARD_ENERGY_DISTRIBUTION) {
    return {
      title: standard.name,
      className: standard.className ?? 'white-header-text',
      children: standard.data.map((meal: any) =>
        generateDataColumns(
          {
            ...meal,
            key: `${standard.key}${meal.key}`
          },
          standard.className,
          editingRowId,
          editedRows,
          setEditedRows,
          listAge
        )
      )
    };
  }

  if (
    standard.key === NutritionStandardKey.STANDARD_FRUIT ||
    standard.key === NutritionStandardKey.STANDARD_VEGETABLE ||
    standard.key === NutritionStandardKey.STANDARD_SALT
  ) {
    return {
      title: standard.name,
      className: standard.className ?? 'white-header-text',
      children: generateMealsDayColumns(
        standard.key,
        standard.className,
        editingRowId,
        editedRows,
        setEditedRows,
        listAge
      )
    };
  }

  if (
    [
      NutritionStandardKey.STANDARD_PLG_BREAKFAST,
      NutritionStandardKey.STANDARD_PLG_LUNCH,
      NutritionStandardKey.STANDARD_PLG_AFTERNOON,
      NutritionStandardKey.STANDARD_PLG_DINNER,
      NutritionStandardKey.STANDARD_PLG_EXTRA1,
      NutritionStandardKey.STANDARD_PLG_EXTRA2
    ].includes(standard.key)
  ) {
    return {
      title: standard.name,
      className: standard.className,
      children: standard.data.map((unit: any) =>
        generateUnitNutritionColumns(
          {
            key: `${standard.key}${unit.key}`,
            name: unit.name,
            data: unit.data
          },
          standard.className,
          editingRowId,
          editedRows,
          setEditedRows,
          listAge
        )
      )
    };
  }

  // fallback
  return {
    title: standard.name,
    children: []
  };
};

const COMMON_ALIGN = 'center';
const COMMON_CLASS = 'grayStyle';

const createColumn = (
  title: string,
  key: string,
  className?: string,
  editingRowId?: string | number | null,
  editedRows?: Record<string, any>,
  setEditedRows?: React.Dispatch<React.SetStateAction<Record<string, any>>>,
  options?: OptionType[],
  placeholder?: string,
  width?: number
): TableColumnsType<any>[number] => ({
  title,
  dataIndex: key,
  key,
  align: COMMON_ALIGN,
  className: className ?? COMMON_CLASS,
  width,
  render: (_: any, record: any) =>
    editingRowId && editedRows && setEditedRows
      ? renderEditableCell({
        record,
        keyName: key,
        editingRowId,
        editedRows,
        setEditedRows,
        options,
        placeholder
      })
      : record[key]
});

// Create column with decimal formatting (display with comma, send with dot)
const createColumnWithDecimalFormat = (
  title: string,
  key: string,
  className?: string,
  editingRowId?: string | number | null,
  editedRows?: Record<string, any>,
  setEditedRows?: React.Dispatch<React.SetStateAction<Record<string, any>>>,
  options?: OptionType[],
  placeholder?: string,
  width?: number
): TableColumnsType<any>[number] => ({
  title,
  dataIndex: key,
  key,
  align: COMMON_ALIGN,
  className: className ?? COMMON_CLASS,
  width,
  render: (_: any, record: any) =>
    editingRowId && editedRows && setEditedRows
      ? renderEditableCell({
        record,
        keyName: key,
        editingRowId,
        editedRows,
        setEditedRows,
        options,
        placeholder,
        isDecimalFormat: true
      })
      : formatNumberDisplay(record[key])
});

export default function NutritionStandardsManagementTable(
  props: NutritionStandardsManagementTableProps
) {
  const dispatch = useAppDispatch();
  const { isAddNew, setIsAddNew } = props;
  const [filter, setFilter] = useState<FilterNutritionStandard>({});
  const pagination = useAppSelector(watchNutritionStandardsPagination);
  const listAge = useAppSelector(watchListAgeNutritionStandard);
  const listHealthStatus = useAppSelector(
    watchListHealthStatusNutritionStandard
  );
  const listGender = useAppSelector(watchListGenderNutritionStandard);
  const listPhysicalActivity = useAppSelector(
    watchListPhysicalActivityNutritionStandard
  );
  const listHealthStatusZCore = useAppSelector(
    watchListHealthStatusZCoreNutritionStandard
  );
  const listNutritionStandard = useAppSelector(
    watchFilteredNutritionalStandards
  );
  const listBreastMilkLevel = useAppSelector(watchListBreastMilkLevel);

  const messageInfo = useAppSelector(watchMessage);

  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editedRows, setEditedRows] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const loading = useAppSelector(watchNutritionLoading);


  const getBmiTypeByAgeId = useCallback((ageId: string) => {
    const found = listAge.find((item) => item.id === ageId);
    return found ? found.healthStatusBmiType || null : null;
  }, [listAge]);

  const handleAgeChange = useCallback((rowId: string, ageId: string) => {
    const bmiType = getBmiTypeByAgeId(ageId);
    const foundAge = listAge.find((age: any) => age.id === ageId);
    if (foundAge && UNDER_24_MONTHS_AGE_IDS_EXCLUDE_DT00.includes(foundAge.id as typeof UNDER_24_MONTHS_AGE_IDS_EXCLUDE_DT00[number])) {
      dispatch(getBreastMilkLevels(ageId));
    }
    setEditedRows((prev) => {
      const prevRow = prev[rowId] || {};
      return {
        ...prev,
        [rowId]: {
          ...prevRow,
          bmiType,
          ageRangeId: ageId,
        }
      };
    });
    if (bmiType) {
      dispatch(getListHealthStatusZCoreNutritionStandard({ type: bmiType, option: 'NUTRITION_STANDARD' }));
    }
  }, [getBmiTypeByAgeId, dispatch, listAge]);

  const handlePageChange = useCallback(({ offset = 1, pageSize = pagination.limitPage, filter: customFilter }: { offset?: number; pageSize?: number; filter?: FilterNutritionStandard }) => {
    dispatch(getFilteredNutritionStandards({ ...(customFilter ?? filter), offset, limit: pageSize }));
  }, [dispatch, filter, pagination.limitPage]);

  useEffect(() => {
    handlePageChange({});
  }, [handlePageChange]);

  useEffect(() => {
    dispatch(getListAgeNutritionStandard());
    dispatch(getListHealthStatusNutritionStandard());
    dispatch(getListGenderNutritionStandard());
    dispatch(getListPhysicalActivityNutritionStandard());
    // dispatch(getFilteredNutritionStandards());
    dispatch(getListHealthStatusZCoreNutritionStandard({ type: 'ALL', option: 'NUTRITION_STANDARD' }));
  }, [dispatch]);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );
      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo]);

  const handleSearch = (values: FilterNutritionStandard) => {
    const isReset = Object.keys(values).length === 0;
    const newFilter = isReset ? {} : { ...filter, ...values };
    setFilter(newFilter);
    handlePageChange({ offset: 1, pageSize: pagination.limitPage, filter: newFilter });
  };

  const ageDishGroupOptions = listAge.map((item) => ({
    value: item.id,
    label: item.description
  }));

  const heathStatusGroupOptions = listHealthStatus.map((item) => ({
    value: item.healthStatusId,
    label: item.displayName
  }));

  const genderGroupOptions = listGender.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const physicalActivityGroupOptions = listPhysicalActivity.map((item) => ({
    value: item.id,
    label: item.name
  }));

  const healthStatusZCoreGroupOptions = listHealthStatusZCore.map((item) => ({
    value: item.id,
    label: item.description
  }));

  const breastMilkLevelOptions = useMemo(() => {
    return listBreastMilkLevel.map((item) => ({
      value: item.id,
      label: item.description
    }));
  }, [listBreastMilkLevel]);

  const handleSaveRow = useCallback(
    async (rowId: string) => {
      const editedData = editedRows[rowId];
      if (!editedData) return;

      const originalRecord = isAddNew
        ? newRowStandardNutrition
        : listNutritionStandard.map(mapApiToFilteredNutritionStandard).find(item => item.id === rowId) || {};

      const fullData = { ...originalRecord, ...editedData };

      const requiredExtraKeys = [
        // Bữa trưa
        'standardPlgLunchPpercentMin', 'standardPlgLunchPpercentMax',
        'standardPlgLunchPgramMin', 'standardPlgLunchPgramMax',
        'standardPlgLunchLpercentMin', 'standardPlgLunchLpercentMax',
        'standardPlgLunchLgramMin', 'standardPlgLunchLgramMax',
        'standardPlgLunchGpercentMin', 'standardPlgLunchGpercentMax',
        'standardPlgLunchGgramMin', 'standardPlgLunchGgramMax',
        // Bữa tối
        'standardPlgDinnerPpercentMin', 'standardPlgDinnerPpercentMax',
        'standardPlgDinnerPgramMin', 'standardPlgDinnerPgramMax',
        'standardPlgDinnerLpercentMin', 'standardPlgDinnerLpercentMax',
        'standardPlgDinnerLgramMin', 'standardPlgDinnerLgramMax',
        'standardPlgDinnerGpercentMin', 'standardPlgDinnerGpercentMax',
        'standardPlgDinnerGgramMin', 'standardPlgDinnerGgramMax',
        // Bữa phụ 1
        'standardPlgExtra1PpercentMin', 'standardPlgExtra1PpercentMax',
        'standardPlgExtra1PgramMin', 'standardPlgExtra1PgramMax',
        'standardPlgExtra1LpercentMin', 'standardPlgExtra1LpercentMax',
        'standardPlgExtra1LgramMin', 'standardPlgExtra1LgramMax',
        'standardPlgExtra1GpercentMin', 'standardPlgExtra1GpercentMax',
        'standardPlgExtra1GgramMin', 'standardPlgExtra1GgramMax',
        // Muối
        'standardSaltDay', 'standardSaltBt', 'standardSaltBs', 'standardSaltBtr', 'standardSaltBc', 'standardSaltBp',
        // Đơn vị sữa/ngày (không tính sữa mẹ) (ml/ngày)
        'milkUnitsPerDay',
      ];
      requiredExtraKeys.forEach(key => {
        if (!Object.prototype.hasOwnProperty.call(fullData, key)) {
          fullData[key] = null;
        }
      });

      const requiredFields: string[] = [];
      const invalidFields: string[] = [];
      const allKeys = Object.keys(fullData);
      for (const key of allKeys) {
        const cellProps = getEditableCellProps({
          keyName: key,
          record: fullData,
          editedRows,
          listAge
        });

        if (cellProps.required) {
          const value = fullData[key];
          const isEmpty =
            value === undefined ||
            value === null ||
            (typeof value === 'string' && value.trim() === '') ||
            (Array.isArray(value) && value.length === 0);
          if (isEmpty) {
            requiredFields.push(key);
          }
        }

        // Check validation function
        if (cellProps.validate) {
          const value = fullData[key];
          if (value !== undefined && value !== null && value !== '') {
            if (!cellProps.validate(value)) {
              invalidFields.push(key);
            }
          }
        }
      }
      if (invalidFields.length > 0) {
        return;
      }
      if (requiredFields.length > 0) {
        setTimeout(() => {
          const firstKey = requiredFields[0];
          const selector =
            `[data-rowid="${rowId}"][data-keyname="${firstKey}"]` +
            ', [name="' + firstKey + '"]';
          const el = document.querySelector(selector) as HTMLElement;
          if (el) {
            if (el.tagName === 'INPUT') {
              el.focus();
            } else {
              const input = el.querySelector('input');
              if (input) input.focus();
              else el.focus?.();
            }
          }
        }, 100);

        const ageId = fullData.ageRangeId;
        const isMilkUnitsPerDayMissing = requiredFields.includes('milkUnitsPerDay');
        const isDT07 = ageId === 'DT07';

        if (isMilkUnitsPerDayMissing && isDT07) {
          message.warning('Giá trị bắt buộc');
        } else {
          message.warning('Vui lòng nhập giá trị!');
        }
        return;
      }

      setIsSaving(true);
      const payload = mapEditedDataToPayload(fullData);
      try {
        if (rowId === newRowStandardNutritionId) {
          // THÊM MỚI
          await dispatch(createNutritionStandard({ payload })).unwrap();
        } else {
          // CHỈNH SỬA
          await dispatch(
            editNutritionStandard({ id: editedData.id, payload })
          ).unwrap();
        }
        const newFilter = { ...filter };
        dispatch(getFilteredNutritionStandards(newFilter));

        setEditedRows((prev) => {
          const newData = { ...prev };
          delete newData[rowId];
          return newData;
        });

        setEditingRowId(null);
        setIsAddNew();
      } catch (error) {
        console.error('❌ Lỗi khi lưu:', error);
      } finally {
        setIsSaving(false);
      }
    },
    [dispatch, editedRows, filter, setIsAddNew, listAge, isAddNew, listNutritionStandard]
  );

  const handleDeleteRow = useCallback(
    async (id: number) => {
      try {
        await dispatch(deleteNutritionStandard({ id })).unwrap();
        const remainingRecords = listNutritionStandard.length - 1;
        if (remainingRecords === 0 && pagination.curPage > 1) {
          dispatch(getFilteredNutritionStandards({
            ...filter,
            offset: pagination.curPage - 1,
            limit: pagination.limitPage
          }));
        } else {
          dispatch(getFilteredNutritionStandards({
            ...filter,
            offset: pagination.curPage,
            limit: pagination.limitPage
          }));
        }
      } catch (error) {
        console.error('❌ Lỗi khi xoá:', error);
      }
    },
    [dispatch, filter, pagination.curPage, pagination.limitPage, listNutritionStandard.length]
  );

  const confirmDeleteRow = useCallback((id: number) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xóa tiêu chuẩn dinh dưỡng này không?',
      content: (
        <em>Lưu ý: Dữ liệu đã xóa sẽ không thể khôi phục lại được.</em>
      ),
      okText: 'Xóa',
      width: 450,
      cancelText: 'Hủy',
      onOk: () => handleDeleteRow(id)
    });
  }, [handleDeleteRow]);

  const allColumns: TableColumnsType<any> = useMemo(() => {
    const baseColumns: TableColumnsType<any> = [
      {
        title: 'Thao tác',
        key: 'action',
        className: 'border-black-column white-header-text',
        width: 100,
        fixed: 'left',
        render: (_: any, record: any) => {
          const isEditing = editingRowId === record.id;

          return isEditing ? (
            <Row gutter={4} wrap={false}>
              <Col>
                <Tooltip title='Lưu'>
                  <ButtonAnt
                    type='primary'
                    icon={<SaveOutlined />}
                    loading={isSaving}
                    disabled={isSaving}
                    onClick={() => handleSaveRow(record.id)}
                  />
                </Tooltip>
              </Col>
              <Col>
                <Tooltip title='Huỷ'>
                  <ButtonAnt
                    icon={<CloseOutlined />}
                    onClick={() => {
                      setEditingRowId(null);
                      setEditedRows((prev) => {
                        const newData = { ...prev };
                        delete newData[record.id];
                        return newData;
                      });
                    }}
                  />
                </Tooltip>
              </Col>
            </Row>
          ) : (
            <Row gutter={4} wrap={false}>
              <Col>
                <Tooltip title='Cập nhật'>
                  <ButtonAnt
                    type='primary'
                    icon={<EditOutlined />}
                    onClick={() => {
                      const ageId = record.ageRangeId;
                      const foundAge = listAge.find((item) => item.id === ageId);
                      const bmiType = foundAge ? foundAge.healthStatusBmiType : null;
                      setEditingRowId(record.id);
                      const formattedRecord = formatRecordForEdit(record);
                      setEditedRows((prev) => ({
                        ...prev,
                        [record.id]: {
                          ...formattedRecord,
                          bmiType,
                          // handle breastMilkLevel
                          breastMilkLevel: record.breastMilkLevel !== undefined
                            ? (typeof record.breastMilkLevel === 'object' && record.breastMilkLevel !== null
                              ? record.breastMilkLevel.id
                              : record.breastMilkLevel)
                            : null
                        }
                      }));
                      if (bmiType) {
                        dispatch(getListHealthStatusZCoreNutritionStandard({ type: bmiType, option: 'NUTRITION_STANDARD' }));
                      }
                      if (foundAge && UNDER_24_MONTHS_AGE_IDS_EXCLUDE_DT00.includes(foundAge.id as typeof UNDER_24_MONTHS_AGE_IDS_EXCLUDE_DT00[number])) {
                        dispatch(getBreastMilkLevels(ageId));
                      }
                    }}
                  />
                </Tooltip>
              </Col>
              <Col>
                <Tooltip title='Xoá'>
                  <ButtonAnt
                    type='primary'
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => confirmDeleteRow(record.id)}
                  />
                </Tooltip>
              </Col>
            </Row>
          );
        }
      },
      {
        title: 'STT',
        key: 'index',
        fixed: 'left',
        width: 100,
        className: 'white-header-text',
        render: (_: any, __: any, index: number) =>
          (pagination.curPage - 1) * pagination.limitPage + index + 1
      },
      {
        title: 'Độ tuổi',
        dataIndex: 'ageRangeId',
        key: 'ageRangeId',
        fixed: 'left',
        width: 100,
        align: 'center',
        className: 'white-header-text',
        render: (_: any, record: any) => {
          const isEditing = editingRowId === record.id;
          if (!isEditing) {
            return record.ageRangeDescription || '';
          }
          return renderEditableCell({
            record,
            keyName: 'ageRangeId',
            options: ageDishGroupOptions,
            editingRowId,
            editedRows,
            setEditedRows,
            onAgeChange: handleAgeChange,
            listAge: listAge,
            placeholder: 'Chọn đối tượng'
          });
        }
      },
      {
        title: 'Tình trạng sức khỏe (khỏe mạnh/ bệnh lý gì)',
        dataIndex: 'healthStatusIds',
        key: 'healthStatusIds',
        align: 'center',
        width: 100,
        fixed: 'left',
        className: 'white-header-text',
        render: (_: any, record: any) => {
          const isEditing = editingRowId === record.id;
          if (!isEditing) {
            const healthStatusIds = record.healthStatusIds || [];
            const displayNames = healthStatusIds
              .map((id: string) => {
                const found = heathStatusGroupOptions.find(opt => opt.value === id);
                return found ? found.label : '';
              })
              .filter(Boolean)
              .join(', ');
            return displayNames || '';
          }
          return renderEditableCell({
            record,
            keyName: 'healthStatusIds',
            options: heathStatusGroupOptions,
            editingRowId,
            editedRows,
            setEditedRows,
            listAge: listAge,
            placeholder: 'Tình trạng sức khỏe'
          });
        }
      },
      {
        title: 'Giới tính',
        dataIndex: 'genderId',
        key: 'genderId',
        align: 'center',
        width: 100,
        className: 'white-header-text',
        render: (_: any, record: any) =>
          renderEditableCell({
            record,
            keyName: 'genderId',
            options: genderGroupOptions,
            editingRowId,
            editedRows,
            setEditedRows,
            listAge: listAge,
            placeholder: 'Nữ'
          })
      },
      {
        title: 'Cân nặng áp dụng tiêu chuẩn (kg)',
        align: 'center',
        width: 200,
        className: 'white-header-text',
        children: [
          {
            title: 'Min',
            dataIndex: 'weightMin',
            key: 'weightMin',
            width: 100,
            align: 'center',
            className: 'white-header-text',
            render: (_: any, record: any) =>
              renderEditableCell({
                record,
                keyName: 'weightMin',
                editingRowId,
                editedRows,
                setEditedRows,
                listAge: listAge,
                placeholder: 'Từ'
              })
          },
          {
            title: 'Max',
            dataIndex: 'weightMax',
            key: 'weightMax',
            width: 100,
            align: 'center',
            className: 'white-header-text',
            render: (_: any, record: any) =>
              renderEditableCell({
                record,
                keyName: 'weightMax',
                editingRowId,
                editedRows,
                setEditedRows,
                listAge: listAge,
                placeholder: 'Đến'
              })
          }
        ]
      },
      {
        title: 'Tình trạng dinh dưỡng theo Zscore, cân nặng theo chiều dài/chiều cao đối với trẻ dưới 60 tháng',
        key: 'nutritionStatusZscore',
        dataIndex: 'nutritionStatusZscore',
        align: 'center',
        width: 200,
        className: 'white-header-text',
        render: (_: any, record: any) => {
          const isEditing = editingRowId === record.id;
          if (isEditing) {
            const bmiType = editedRows[record.id]?.bmiType;
            return bmiType === AGE_TYPE_CONST.ZSCORE
              ? renderEditableCell({
                record,
                keyName: 'nutritionStatusZscore',
                options: healthStatusZCoreGroupOptions || [],
                editingRowId,
                editedRows,
                setEditedRows,
                listAge: listAge,
                placeholder: 'Tình trạng'
              })
              : <Select disabled style={{ width: 160 }} />;
          }
          return record.nutritionStatusZscoreName || '';
        }
      },
      {
        title:
          'Tình trạng dinh dưỡng theo BMI đối với trẻ từ 5 (trên 60 tháng) đến 19 tuổi',
        align: 'center',
        width: 200,
        className: 'white-header-text',
        key: 'nutritionStatusBmiChild',
        dataIndex: 'nutritionStatusBmiChild',
        render: (_: any, record: any) => {
          const isEditing = editingRowId === record.id;
          if (isEditing) {
            const bmiType = editedRows[record.id]?.bmiType;
            return bmiType === AGE_TYPE_CONST.BMI_CHILD
              ? renderEditableCell({
                record,
                keyName: 'nutritionStatusBmiChild',
                options: healthStatusZCoreGroupOptions || [],
                editingRowId,
                editedRows,
                setEditedRows,
                listAge: listAge,
                placeholder: 'Tình trạng'
              })
              : <Select disabled style={{ width: 160 }} />;
          }
          return record.nutritionStatusChildName || '';
        }
      },
      {
        title:
          'Tình trạng dinh dưỡng theo BMI đối với người trưởng thành (>19 tuổi) & người cao tuổi',
        align: 'center',
        width: 200,
        className: 'white-header-text',
        key: 'nutritionStatusBmiAdult',
        dataIndex: 'nutritionStatusBmiAdult',
        render: (_: any, record: any) => {
          const isEditing = editingRowId === record.id;
          if (isEditing) {
            const bmiType = editedRows[record.id]?.bmiType;
            return bmiType === AGE_TYPE_CONST.BMI_ADULT
              ? renderEditableCell({
                record,
                keyName: 'nutritionStatusBmiAdult',
                options: healthStatusZCoreGroupOptions || [],
                editingRowId,
                editedRows,
                setEditedRows,
                listAge: listAge,
                placeholder: 'Tình trạng'
              })
              : <Select disabled style={{ width: 160 }} />;
          }
          return record.nutritionStatusAdultName || '';
        }
      },
      {
        title: 'Hoạt động thể lực',
        key: 'physicalActivityLevelName',
        dataIndex: 'physicalActivityLevelName',
        align: 'center',
        width: 100,
        className: 'white-header-text',
        render: (_: any, record: any) => {
          return renderEditableCell({
            record,
            keyName: 'physicalActivityId',
            options: physicalActivityGroupOptions,
            editingRowId,
            editedRows,
            setEditedRows,
            listAge: listAge
          })
        }
      },
    ];

    const energyColumns: TableColumnsType<any> = energyColumnName.map(
      ([title, key, className]) =>
        createColumn(
          title,
          key,
          className,
          editingRowId,
          editedRows,
          setEditedRows,
          undefined,
          undefined,
          150
        )
    );
    const dailyEnergyStandardColumn: TableColumnsType<any> = [
      {
        title: 'Tiêu chuẩn năng lượng/ngày (kcal)',
        align: 'center',
        className: 'redStyle',
        width: 200,
        children: [
          {
            title: 'Min',
            dataIndex: 'weightMin',
            key: 'weightMin',
            width: 100,
            align: 'center',
            className: 'redStyle',
            render: (_: any, record: any) =>
              renderEditableCell({
                record,
                keyName: 'energyMin',
                editingRowId,
                editedRows,
                setEditedRows,
                listAge: listAge
              })
          },
          {
            title: 'Max',
            dataIndex: 'weightMax',
            width: 100,
            key: 'weightMax',
            align: 'center',
            className: 'redStyle',
            render: (_: any, record: any) =>
              renderEditableCell({
                record,
                keyName: 'energyMax',
                editingRowId,
                editedRows,
                setEditedRows,
                listAge: listAge
              })
          }
        ]
      },
      {
        title: 'Năng lượng từ thức ăn bổ sung',
        align: 'center',
        className: 'redStyle',
        width: 200,
        children: [
          {
            title: 'Min',
            dataIndex: 'complementaryFoodEnergyMin',
            key: 'complementaryFoodEnergyMin',
            width: 100,
            align: 'center',
            className: 'redStyle',
            render: (_: any, record: any) => renderEditableCell({
              record,
              keyName: 'complementaryFoodEnergyMin',
              editingRowId,
              editedRows,
              setEditedRows,
              listAge: listAge
            })
          },
          {
            title: 'Max',
            dataIndex: 'complementaryFoodEnergyMax',
            key: 'complementaryFoodEnergyMax',
            align: 'center',
            width: 100,
            className: 'redStyle',
            render: (_: any, record: any) => renderEditableCell({
              record,
              keyName: 'complementaryFoodEnergyMax',
              editingRowId,
              editedRows,
              setEditedRows,
              listAge: listAge
            })
          }
        ]
      },
      {
        title: 'Mức bú sữa mẹ chỉ dành cho trẻ dưới 24 tháng',
        dataIndex: 'breastMilkLevel',
        key: 'breastMilkLevel',
        align: 'center',
        width: 150,
        className: 'redStyle',
        render: (_: any, record: any) => {
          const isEditing = editingRowId === record.id;
          if (!isEditing) {
            return record.breastMilkLevelValue
              ? record.breastMilkLevelValue
              : '';
          }
          return renderEditableCell({
            record,
            keyName: 'breastMilkLevel',
            editingRowId,
            editedRows,
            setEditedRows,
            listAge: listAge,
            options: breastMilkLevelOptions
          });
        }
      },
      {
        title: 'Năng lượng từ mức bú sữa mẹ dành cho trẻ dưới 24 tháng',
        dataIndex: 'breastMilkEnergy',
        key: 'breastMilkEnergy',
        width: 150,
        align: 'center',
        className: 'redStyle',
        render: (_: any, record: any) => renderEditableCell({
          record,
          keyName: 'breastMilkEnergy',
          editingRowId,
          editedRows,
          setEditedRows,
          listAge: listAge
        })
      },
      {
        title: 'Đơn vị sữa/ngày (không tính sữa mẹ) (ml/ngày)',
        dataIndex: 'milkUnitsPerDay',
        key: 'milkUnitsPerDay',
        align: 'center',
        width: 150,
        className: 'redStyle',
        render: (_: any, record: any) => renderEditableCell({
          record,
          keyName: 'milkUnitsPerDay',
          editingRowId,
          editedRows,
          setEditedRows,
          listAge: listAge
        })
      }
    ];

    const dynamicColumns: TableColumnsType<any> = allNutritionStandard.map(
      (standard) =>
        generateNutritionColumns(
          standard,
          editingRowId,
          editedRows,
          setEditedRows,
          listAge
        )
    );

    const extraColumns: TableColumnsType<any> = [
      {
        title: 'Tổng số nguyên liệu/ngày (≥ngày)',
        dataIndex: 'totalDay',
        key: 'totalDay',
        align: 'center',
        width: 100,
        className: 'grayStyle',
        render: (_: any, record: any) =>
          renderEditableCell({
            record,
            keyName: 'totalDay',
            editingRowId,
            editedRows,
            setEditedRows,
            listAge: listAge
          })
      },
      {
        title: 'Tổng số nguyên liệu/bữa trưa (≥ngày)',
        dataIndex: 'totalLunch',
        key: 'totalLunch',
        width: 100,
        align: 'center',
        className: 'grayStyle',
        render: (_: any, record: any) =>
          renderEditableCell({
            record,
            keyName: 'totalLunch',
            editingRowId,
            editedRows,
            setEditedRows,
            listAge: listAge
          })
      },
      {
        title: 'Tổng số nguyên liệu/bữa tối (≥ngày)',
        dataIndex: 'totalDinner',
        key: 'totalDinner',
        align: 'center',
        width: 100,
        className: 'grayStyle',
        render: (_: any, record: any) =>
          renderEditableCell({
            record,
            keyName: 'totalDinner',
            editingRowId,
            editedRows,
            setEditedRows,
            listAge: listAge
          })
      },
      createColumn(
        'Đường (g/ngày)',
        'sugarStandardDay',
        'grayStyle',
        editingRowId,
        editedRows,
        setEditedRows,
        undefined,
        undefined,
        100
      ),

      {
        title: 'Nước (ml/ngày)',
        className: COMMON_CLASS,
        children: generateMinMaxColumns({
          baseKey: 'waterStandardDay',
          className: 'grayStyle',
          editingRowId,
          editedRows,
          setEditedRows,
          listAge: listAge
        })
      },

      createColumnWithDecimalFormat(
        'Chất xơ (g/ngày)',
        'fiberStandardDay',
        'grayStyle',
        editingRowId,
        editedRows,
        setEditedRows,
        undefined,
        undefined,
        100
      ),

      ...[
        ['Kali (g/ngày)', 'kaliStandardDay'],
        ['Cholesterol (mg/ngày)', 'cholesterolStandardDay'],
        ['Natri (mg/ngày)', 'natriStandardDay'],
        ['Clo (mg/ngày)', 'cloStandardDay'],
        ['Canxi (mg/ngày)', 'calciStandardDay'],
        ['Phospho (mg/ngày)', 'phosphoStandardDay'],
        ['Magie (mg/ngày)', 'magieStandardDay'],
        ['Sắt (mg/ngày)', 'ironStandardDay'],
        ['Kẽm (mg/ngày)', 'zincStandardDay'],
        ['Iod (µg/ngày)', 'iodStandardDay'],
        ['Selen (µg/ngày)', 'selenStandardDay'],
        ['Đồng (µg/ngày)', 'copperStandardDay'],
        ['Crom (µg/ngày)', 'cromStandardDay'],
        ['Mangan (µg/ngày)', 'manganStandardDay'],
        ['Fluo (mg/ngày)', 'fluorStandardDay'],
        ['Vitamin A (µg/ngày)', 'vitaminAStandardDay'],
        ['Vitamin D (µg/ngày)', 'vitaminDStandardDay'],
        ['Vitamin E (mg/ngày)', 'vitaminEStandardDay'],
        ['Vitamin K (µg/ngày)', 'vitaminKStandardDay'],
        ['Vitamin B1 (mg/ngày)', 'vitaminB1StandardDay'],
        ['Vitamin B2 (mg/ngày)', 'vitaminB2StandardDay'],
        ['Vitamin B3 (mg/ngày)', 'vitaminB3StandardDay'],
        ['Vitamin B5 (mg/ngày)', 'vitaminB5StandardDay'],
        ['Vitamin B6 (mg/ngày)', 'vitaminB6StandardDay'],
        ['Vitamin B9 (µg/ngày)', 'vitaminB9StandardDay'],
        ['Vitamin B12 (µg/ngày)', 'vitaminB12StandardDay'],
        ['Biotin (µg/ngày)', 'biotinStandardDay'],
        ['Choline (mg/ngày)', 'cholineStandardDay'],
        ['Vitamin C (mg/ngày)', 'vitaminCStandardDay'],
        ['Total unsaturated fat (g/ngày)', 'totalUnsaturated']
      ].map(([title, key]) =>
        createColumnWithDecimalFormat(
          title,
          key,
          'grayStyle',
          editingRowId,
          editedRows,
          setEditedRows,
          undefined,
          undefined,
          100
        )
      )
    ];

    const reserveColumns: TableColumnsType<any> = Array.from(
      { length: 10 },
      (_, i) =>
        generateReserveColumns(
          i + 1,
          'grayStyle',
          editingRowId,
          editedRows,
          setEditedRows
        )[0]
    );

    const dateTimeColumns: TableColumnsType<any> = [
      {
        title: 'Ngày tạo',
        dataIndex: 'createdAt',
        key: 'createdAt',
        align: 'center' as const,
        className: 'white-header-text',
        width: 150,
        render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm')
      },
      {
        title: 'Ngày cập nhật',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        align: 'center' as const,
        className: 'white-header-text',
        width: 150,
        render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm')
      }
    ];

    return [
      ...baseColumns,
      ...energyColumns,
      ...dailyEnergyStandardColumn,
      ...dynamicColumns,
      ...extraColumns,
      ...reserveColumns,
      ...dateTimeColumns
    ];
  }, [editingRowId, editedRows, handleSaveRow, listAge, dispatch, confirmDeleteRow, pagination.curPage, pagination.limitPage, ageDishGroupOptions, handleAgeChange, heathStatusGroupOptions, genderGroupOptions, healthStatusZCoreGroupOptions, physicalActivityGroupOptions, breastMilkLevelOptions, isSaving]);

  useEffect(() => {
    if (isAddNew) {
      setEditingRowId(newRowStandardNutritionId);
      setEditedRows((prev) => ({
        ...prev,
        [newRowStandardNutritionId]: newRowStandardNutrition
      }));
    }
  }, [isAddNew]);

  return (
    <div css={rootStyles}>
      <NutritionStandardsManagementSearcher
        onSearch={handleSearch}
        listDishAge={listAge}
        listHealthStatus={listHealthStatus}
        listGender={listGender}
        listPhysicalActivity={listPhysicalActivity}
        listHealthStatusZCore={listHealthStatusZCore}
      />
      <Row css={boxStyles}>
        <Col>
          <Typography.Title css={titleStyles} level={5}>
            Danh sách tiêu chuẩn dinh dưỡng
          </Typography.Title>
        </Col>
      </Row>
      <div css={boxStyles}>

        <Table<any>
          columns={allColumns}
          loading={loading}
          enableSticky
          stickyOffsetHeader={60}
          dataSource={[
            ...(isAddNew ? [newRowStandardNutrition] : []),
            ...listNutritionStandard.map(mapApiToFilteredNutritionStandard)
          ]}
          pagination={{
            showQuickJumper: true,
            showSizeChanger: true,
            current: pagination.curPage,
            pageSize: pagination.limitPage,
            total: pagination.totalRows + (isAddNew ? 1 : 0),
            onChange: (page, pageSize) => handlePageChange({ offset: page, pageSize, filter }),
          }}
        />
      </div>
    </div>
  );
}

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }

  .ant-table-thead > tr > th {
    width: 10rem;
  }

  .ant-table-tbody > tr > td {
    color: var(--black-color) !important;
  }

  .ant-table-thead > tr > th.grayStyle {
    color: var(--white-color);
    background: rgba(100, 100, 100, 1) !important;
  }

  .ant-table-thead > tr > th.redStyle {
    background-color: var(--red-color) !important;
    color: var(--white-color);
  }

  .ant-table-thead > tr > th.blueStyle {
    background-color: var(--blue-soft-color) !important;
    color: var(--white-color);
  }

  .ant-table-thead > tr > th.yellowStyle {
    background-color: var(--yellow-dark-color) !important;
    color: var(--white-color);
  }

  .ant-table-thead > tr > th.redStyle {
    background-color: var(--red-color) !important;
    color: var(--white-color);
  }
  .ant-table-thead > tr > th.purpleStyle {
    background-color: var(--purple-color) !important;
    color: var(--white-color);
  }

  .ant-table-thead > tr > th.extraStyle {
    background-color: var(--blue-soft-color) !important;
    color: var(--white-color);
  }

  .ant-table-thead > tr > th {
    background: var(--blue-dark-color) !important;
  }

  .ant-table-cell::before {
    display: none;
  }

  .ant-table-cell.border-black-column {
    box-shadow: 0 0px 4px rgba(0, 0, 0, 0.2);
    z-index: 1;
    position: relative;
  }

  .ant-table-thead > tr > th:not(.border-black-column) {
    border: 1px solid white !important;
  }

  .white-header-text {
    color: white !important;
  }
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  font-weight: bold !important;
  line-height: 2.2rem !important;
`;
